import { defineStore } from 'pinia';
import axios from 'axios';
import { useAuthStore } from './auth';
import { useCatalogStore } from './catalog';

const STRAPI_URL = import.meta.env.VITE_STRAPI_URL ?? 'http://localhost:1337';
const MAX_PAGE_SIZE = 100;

const normalizeRelation = (payload) => {
	if (!payload) return null;
	const data = payload.data || payload;
	if (!data) return null;
	const attributes = data.attributes || {};
	return {
		id: data.id ?? attributes.id ?? null,
		name: attributes.name ?? data.name ?? '',
	};
};

const normalizeTemplate = (entry) => {
	const attributes = entry?.attributes || entry || {};
	return {
		id: entry?.id ?? attributes.id ?? null,
		documentId: attributes.documentId ?? entry?.documentId ?? null,
		name: attributes.name ?? entry?.name ?? '',
	};
};

const normalizeTemplateItem = (entry) => {
	const attributes = entry?.attributes || entry || {};
	return {
		id: entry?.id ?? attributes.id ?? null,
		documentId: attributes.documentId ?? entry?.documentId ?? null,
		dayIndex: attributes.dayIndex ?? null,
		flatRef: attributes.flatRef ?? '',
		hours: attributes.hours ?? null,
		template: normalizeRelation(attributes.template),
		building: normalizeRelation(attributes.building),
		supplier: normalizeRelation(attributes.supplier),
		service: normalizeRelation(attributes.service),
	};
};

const normalizeHours = (value) => {
	if (value === '' || value === null || value === undefined) return null;
	const num = Number(value);
	if (Number.isNaN(num)) return null;
	if (num < 0) {
		throw new Error('Hours cannot be negative');
	}
	return num;
};

const normalizeDayIndex = (value) => {
	const num = Number(value);
	if (!Number.isFinite(num)) return null;
	const normalized = Math.trunc(num);
	if (normalized < 0 || normalized > 6) return null;
	return normalized;
};

const sortByName = (entries = []) =>
	[...entries].sort((a, b) => (a.name || '').localeCompare(b.name || ''));

export const useJournalAppointmentTemplatesStore = defineStore('journalAppointmentTemplates', {
	state: () => ({
		templates: [],
		items: [],
		activeTemplateId: null,
		isLoadingTemplates: false,
		isLoadingItems: false,
		templatesError: null,
		itemsError: null,
	}),
	getters: {
		templateOptions: (state) => state.templates,
		buildingOptions: () => {
			const catalogStore = useCatalogStore();
			return catalogStore.buildingOptions;
		},
		supplierOptions: () => {
			const catalogStore = useCatalogStore();
			return catalogStore.supplierOptions;
		},
		serviceOptions: () => {
			const catalogStore = useCatalogStore();
			return catalogStore.serviceOptions;
		},
	},
	actions: {
		authHeaders() {
			const authStore = useAuthStore();
			return authStore.token ? authStore.requestHeaders.headers : {};
		},

		async fetchTemplates() {
			if (this.isLoadingTemplates) return this.templates;
			this.isLoadingTemplates = true;
			this.templatesError = null;

			try {
				const response = await axios.get(
					`${STRAPI_URL}/api/journal-appointments-templates`,
					{
						headers: this.authHeaders(),
						params: {
							'sort[0]': 'name:asc',
						},
					},
				);

				const entries = response?.data?.data || [];
				this.templates = entries.map(normalizeTemplate);
				return this.templates;
			} catch (error) {
				this.templatesError =
					error?.response?.data?.error?.message ||
					error.message ||
					'Failed to load templates';
				throw error;
			} finally {
				this.isLoadingTemplates = false;
			}
		},

		async createTemplate({ name }) {
			const trimmed = String(name || '').trim();
			if (!trimmed) {
				throw new Error('Template name is required');
			}
			const response = await axios.post(
				`${STRAPI_URL}/api/journal-appointments-templates`,
				{ data: { name: trimmed } },
				{ headers: this.authHeaders() },
			);
			const created = normalizeTemplate(response?.data?.data || {});
			this.templates = sortByName([...(this.templates || []), created]);
			return created;
		},

		async updateTemplate(documentId, payload) {
			if (!documentId) {
				throw new Error('Missing template identifier');
			}
			const response = await axios.put(
				`${STRAPI_URL}/api/journal-appointments-templates/${documentId}`,
				{ data: payload },
				{ headers: this.authHeaders() },
			);
			const updated = normalizeTemplate(response?.data?.data || {});
			const next = (this.templates || []).map((entry) =>
				entry.documentId === updated.documentId || entry.id === updated.id
					? { ...entry, ...updated }
					: entry,
			);
			this.templates = sortByName(next);
			return updated;
		},

		async deleteTemplate(documentId) {
			if (!documentId) return false;
			await axios.delete(`${STRAPI_URL}/api/journal-appointments-templates/${documentId}`, {
				headers: this.authHeaders(),
			});
			this.templates = (this.templates || []).filter(
				(entry) => entry.documentId !== documentId && entry.id !== documentId,
			);
			if (
				this.activeTemplateId &&
				[String(this.activeTemplateId), String(documentId)].every(Boolean) &&
				String(this.activeTemplateId) === String(documentId)
			) {
				this.activeTemplateId = null;
				this.items = [];
			}
			return true;
		},

		async fetchTemplateItems(templateId) {
			if (!templateId) {
				this.items = [];
				this.activeTemplateId = null;
				return [];
			}
			this.isLoadingItems = true;
			this.itemsError = null;
			this.activeTemplateId = templateId;

			try {
				const collected = [];
				let page = 1;
				let pageCount = 1;
				const params = {
					'sort[0]': 'dayIndex:asc',
					'sort[1]': 'id:asc',
					'filters[template][id][$eq]': templateId,
					'populate[building][fields][0]': 'name',
					'populate[supplier][fields][0]': 'name',
					'populate[service][fields][0]': 'name',
					'populate[template][fields][0]': 'name',
					'pagination[pageSize]': MAX_PAGE_SIZE,
				};

				do {
					const response = await axios.get(
						`${STRAPI_URL}/api/journal-appointments-template-items`,
						{
							headers: this.authHeaders(),
							params: {
								...params,
								'pagination[page]': page,
							},
						},
					);
					const entries = response?.data?.data || [];
					collected.push(...entries);
					const pagination = response?.data?.meta?.pagination || {};
					pageCount = pagination.pageCount ?? page;
					page += 1;
				} while (page <= pageCount);

				this.items = collected.map(normalizeTemplateItem);
				return this.items;
			} catch (error) {
				this.itemsError =
					error?.response?.data?.error?.message ||
					error.message ||
					'Failed to load template items';
				throw error;
			} finally {
				this.isLoadingItems = false;
			}
		},

		async createTemplateItem({
			templateId,
			dayIndex,
			buildingId = null,
			supplierId = null,
			serviceId = null,
			flatRef = '',
			hours = null,
		}) {
			const normalizedDayIndex = normalizeDayIndex(dayIndex);
			if (!templateId || normalizedDayIndex === null) {
				throw new Error('Missing template or invalid day index');
			}
			const payload = {
				template: templateId,
				dayIndex: normalizedDayIndex,
				building: buildingId || null,
				supplier: supplierId || null,
				service: serviceId || null,
				flatRef: flatRef || '',
				hours: normalizeHours(hours),
			};
			const response = await axios.post(
				`${STRAPI_URL}/api/journal-appointments-template-items`,
				{ data: payload },
				{
					headers: this.authHeaders(),
					params: {
						'populate[building][fields][0]': 'name',
						'populate[supplier][fields][0]': 'name',
						'populate[service][fields][0]': 'name',
						'populate[template][fields][0]': 'name',
					},
				},
			);
			return normalizeTemplateItem(response?.data?.data || {});
		},

		async updateTemplateItem(documentId, payload) {
			if (!documentId) {
				throw new Error('Missing template item identifier');
			}
			const normalizedPayload = { ...payload };
			if ('hours' in normalizedPayload) {
				normalizedPayload.hours = normalizeHours(normalizedPayload.hours);
			}
			const response = await axios.put(
				`${STRAPI_URL}/api/journal-appointments-template-items/${documentId}`,
				{ data: normalizedPayload },
				{
					headers: this.authHeaders(),
					params: {
						'populate[building][fields][0]': 'name',
						'populate[supplier][fields][0]': 'name',
						'populate[service][fields][0]': 'name',
						'populate[template][fields][0]': 'name',
					},
				},
			);
			return normalizeTemplateItem(response?.data?.data || {});
		},

		async deleteTemplateItem(documentId) {
			if (!documentId) return false;
			await axios.delete(
				`${STRAPI_URL}/api/journal-appointments-template-items/${documentId}`,
				{ headers: this.authHeaders() },
			);
			return true;
		},
	},
});
